import { addFavorite, deleteFavorite, getMultiCallsDashboard } from '@/api/apiService';
import { Nullable } from '@/types/common';
import { ChatItem } from '@/types/components';
import { formatSimpleTimeAgo } from '@/utils/dateFormatter.js';

export const useChatList = () => {
  const { t } = useI18n();

  const chatList = ref<ChatItem[]>([]);
  const searchParams = ref({
    keyword: '',
    chatTypes: [],
    isFavorite: null,
  });
  const limit = ref<number | null>(null);
  const pagination = ref({
    hasMore: false,
    nextOffset: null,
  });

  const CHAT_TYPE_FILTERS = [
    { value: 'ALL', label: 'label_all' },
    { value: 'MEETING', label: 'label_meeting' },
    { value: 'CONFERENCE', label: 'label_conference' },
    { value: 'GENERAL', label: 'label_general' },
  ] as const;

  const setFilter = async (params) => {
    if (params.keyword !== undefined) {
      searchParams.value.keyword = params.keyword;
    }
    if (params.chatTypes !== undefined) {
      searchParams.value.chatTypes = params.chatTypes;
    }
    if (params.isFavorite !== undefined) {
      searchParams.value.isFavorite = params.isFavorite;
    }

    await fetchUserChatList();
  };

  const setLimit = (number: number) => {
    limit.value = number;
  };

  const fetchUserChatList = async (offset?: Nullable<string>) => {
    try {
      const keyword = searchParams.value.keyword || null;
      const chatTypes = searchParams.value.chatTypes ? searchParams.value.chatTypes : null;
      const isFavorite = searchParams.value.isFavorite ?? null;

      const { data } = await getMultiCallsDashboard(offset, keyword, chatTypes, isFavorite);

      if (!data || data.datas.length === 0) {
        chatList.value = [];
        pagination.value = {
          hasMore: false,
          nextOffset: null,
        };

        return;
      }

      pagination.value = {
        hasMore: data.hasMore,
        nextOffset: data.nextOffset,
      };

      const slicedList = limit.value && limit.value > 0 ? data.datas.slice(0, limit.value) : data.datas;

      const formattedList = slicedList.map((item) => {
        const [time, type] = formatSimpleTimeAgo(item.eventTime);

        return {
          ...item,
          chatType: item.callType.toLowerCase(),
          displayTime: item.eventTime ? `${time}${t(type)} ${t('label_before')}` : '',
        };
      });

      if (offset) {
        chatList.value.push(...formattedList);
      } else {
        chatList.value = formattedList;
      }
    } catch (error) {
      console.error('최근 사용한 통역 조회 실패:', error);
      chatList.value = [];
    }
  };

  const toggleFavorite = async (chat: ChatItem) => {
    try {
      if (chat.isFavorite) {
        await deleteFavorite('QR', chat.code);
      } else {
        await addFavorite('QR', chat.code);
      }

      await fetchUserChatList();
    } catch (error) {
      console.error('북마크 변경 실패: ', error);
    }
  };

  return {
    CHAT_TYPE_FILTERS,
    chatList,
    pagination,
    setFilter,
    setLimit,
    fetchUserChatList,
    toggleFavorite,
  };
};
